import {
  ProductWithQualityInfo,
  ProductWithSides,
  QualityCheck,
  QualityCheckStatus,
  Session,
  SideWithAsset,
} from 'shared-types';

export function getTotalProductsCount(products: ProductWithSides[]) {
  return products.reduce((acc, p) => acc + p.count, 0);
}

export const getFileName = (filename: string) => {
  return filename.split('.').slice(0, -1).join('.');
};

export const sanitizeFileName = (filename: string) => {
  // Split filename into name and extension
  const lastDotIndex = filename.lastIndexOf('.');
  const name = lastDotIndex !== -1 ? filename.substring(0, lastDotIndex) : filename;
  const extension = lastDotIndex !== -1 ? filename.substring(lastDotIndex) : '';

  // Remove or replace problematic characters for R2 storage and file systems
  // Keep alphanumeric, hyphens, underscores, spaces, and dots in extension
  const sanitizedName = name
    .replace(/['"´`¢¬@,;]/g, '') // Remove quotes, currency symbols, and punctuation
    .replace(/[^\w\s.-]/g, '') // Remove any remaining special characters except word chars, spaces, dots, hyphens
    .replace(/^\.+/, '') // Remove leading dots
    .replace(/\s+/g, ' ') // Normalize multiple spaces to single space
    .trim(); // Remove leading/trailing whitespace

  // Ensure we don't end up with an empty filename
  const finalName = sanitizedName || 'file';

  // Add timestamp to prevent overwriting
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-');

  return `${finalName}_${timestamp}${extension}`;
};

export function calculateQualityCheck(
  side: SideWithAsset,
  session: Session,
  totalSidesCount: number,
): QualityCheck {
  const { resizeMetadata, asset } = side;
  const {
    sidesCount,
    productSize,
    options: { minimalDpi },
  } = session;

  if (!resizeMetadata || !asset?.metadata?.page) {
    return {
      scaling: {
        status: 'perfect',
        isValid: true,
        message: '',
        percentage: 100,
        isAspectRatioDistorted: false,
      },
      resolution: {
        status: 'perfect',
        isValid: true,
        message: '',
        actualDpi: minimalDpi,
        minimumDpi: minimalDpi,
      },
      hasWarnings: false,
      hasErrors: false,
    };
  }

  const { scalingPercentage, lowestResolution } = resizeMetadata;

  // Note: Page count checks are handled at product level, not side level

  // Scaling check
  const isScalingPerfect = scalingPercentage >= 99.9 && scalingPercentage <= 100.1;

  // Calculate aspect ratios to determine if scaling is proportional
  const productAspectRatio = Number(productSize.width) / Number(productSize.height);
  const sourceAspectRatio =
    asset.metadata.page?.widthMM && asset.metadata.page?.heightMM
      ? Number(asset.metadata.page.widthMM) / Number(asset.metadata.page.heightMM)
      : null;

  const isAspectRatioDistorted = sourceAspectRatio
    ? Math.abs(productAspectRatio - sourceAspectRatio) / productAspectRatio > 0.05
    : false;

  let scalingStatus: QualityCheckStatus = 'perfect';
  if (!isScalingPerfect) {
    scalingStatus = isAspectRatioDistorted ? 'error' : 'warning';
  }

  // Resolution check
  const resolutionStatus: QualityCheckStatus =
    lowestResolution >= minimalDpi || lowestResolution === -1 ? 'perfect' : 'error';

  const hasWarnings = [scalingStatus, resolutionStatus].includes('warning');
  const hasErrors = [scalingStatus, resolutionStatus].includes('error');

  return {
    scaling: {
      status: scalingStatus,
      isValid: isScalingPerfect,
      message: '',
      percentage: scalingPercentage,
      isAspectRatioDistorted,
    },
    resolution: {
      status: resolutionStatus,
      isValid: lowestResolution >= minimalDpi || lowestResolution === -1,
      message: '',
      actualDpi: lowestResolution,
      minimumDpi: minimalDpi,
    },
    hasWarnings,
    hasErrors,
  };
}

export function getSidesWithWarnings(sides: SideWithAsset[]): number[] {
  return sides
    .filter((side) => side.qualityCheck?.hasWarnings || side.qualityCheck?.hasErrors)
    .map((side) => side.orderNumber);
}

export function updateProductQualityInfo(
  product: ProductWithQualityInfo,
  session: Session,
  totalSidesCount: number,
): ProductWithQualityInfo {
  const updatedSides = product.sides.map((side) => {
    if ('asset' in side && side.asset && side.resizeMetadata) {
      const updatedQualityCheck = calculateQualityCheck(side, session, totalSidesCount);
      return {
        ...side,
        qualityCheck: updatedQualityCheck,
      };
    }
    return side;
  });

  const sidesWithWarnings = getSidesWithWarnings(updatedSides as SideWithAsset[]);

  return {
    ...product,
    sides: updatedSides,
    sidesWithWarnings,
  };
}
